import { useEffect, useRef, useState } from "react";

const GOOGLE_MAPS_SCRIPT = "https://maps.googleapis.com/maps/api/js";
const DEFAULT_MAP_ID = "DEMO_MAP_ID";
const LOAD_TIMEOUT_MS = 12_000;

export interface GoogleMapMarker {
  id: string;
  latitude: number;
  longitude: number;
  label?: string;
}

export interface GoogleMapProps {
  apiKey?: string;
  mapId?: string;
  markers: GoogleMapMarker[];
  center?: { latitude: number; longitude: number };
  zoom?: number;
  height?: number;
  onStatusChange?: (status: "loading" | "ready" | "error", message?: string) => void;
}

interface LatLng { lat: number; lng: number }
interface NativeMap { setCenter: (center: LatLng) => void; setZoom: (zoom: number) => void }
interface NativeMarker { map: NativeMap | null; position: LatLng; title?: string }
interface MapsApi { importLibrary: (name: "maps" | "marker") => Promise<unknown> }
interface MapsWindow extends Window { google?: { maps?: MapsApi }; gm_authFailure?: () => void; __jrniGoogleMapsReady?: () => void }
interface MapsLibrary { Map: new (element: HTMLElement, options: Record<string, unknown>) => NativeMap }
interface MarkerLibrary { AdvancedMarkerElement: new (options: { map: NativeMap; position: LatLng; title?: string }) => NativeMarker }

let mapsLoader: Promise<MapsApi> | null = null;
let googleMapsAuthFailed = false;
const authFailureListeners = new Set<() => void>();

function loadGoogleMaps(apiKey: string): Promise<MapsApi> {
  if (googleMapsAuthFailed) return Promise.reject(new Error("Google Maps authentication failed."));
  if (mapsLoader) return mapsLoader;
  const w = window as MapsWindow;
  if (w.google?.maps?.importLibrary) return (mapsLoader = Promise.resolve(w.google.maps));
  mapsLoader = new Promise<MapsApi>((resolve, reject) => {
    let settled = false;
    const script = document.querySelector<HTMLScriptElement>('script[data-google-maps-loader="google-map"]') ?? document.createElement("script");
    const previousAuthFailure = w.gm_authFailure;
    const finish = (error?: Error) => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timeout);
      script.removeEventListener("error", onError);
      if (w.__jrniGoogleMapsReady === onLoad) delete w.__jrniGoogleMapsReady;
      // Keep the auth failure hook installed after script load: Google can
      // report a rejected key asynchronously while libraries are imported.
      if (error) {
        if (script.dataset.googleMapsLoader === "google-map") script.remove();
        mapsLoader = null;
        reject(error);
        return;
      }
      if (w.google?.maps?.importLibrary) resolve(w.google.maps);
      else { mapsLoader = null; reject(new Error("Google Maps loaded without its API.")); }
    };
    const onAuthFailure = () => {
      googleMapsAuthFailed = true;
      authFailureListeners.forEach((listener) => listener());
      try { previousAuthFailure?.(); } catch { /* preserve the provider error below */ }
      finish(new Error("Google Maps authentication failed."));
    };
    const onLoad = () => w.google?.maps?.importLibrary ? finish() : finish(new Error("Google Maps loaded without its API."));
    const onError = () => finish(new Error("Google Maps could not be loaded."));
    const timeout = window.setTimeout(() => finish(new Error("Google Maps took too long to load.")), LOAD_TIMEOUT_MS);
    w.gm_authFailure = onAuthFailure;
    script.addEventListener("error", onError, { once: true });
    w.__jrniGoogleMapsReady = onLoad;
    if (!script.parentNode) {
      script.async = true;
      script.defer = true;
      script.dataset.googleMapsLoader = "google-map";
      script.src = `${GOOGLE_MAPS_SCRIPT}?key=${encodeURIComponent(apiKey)}&v=weekly&loading=async&callback=__jrniGoogleMapsReady`;
      document.head.appendChild(script);
    } else if (w.google?.maps?.importLibrary) onLoad();
  });
  return mapsLoader;
}

export default function GoogleMap({ apiKey, mapId = DEFAULT_MAP_ID, markers, center, zoom = 15, height = 220, onStatusChange }: GoogleMapProps) {
  const hostRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<NativeMap | null>(null);
  const mapIdRef = useRef<string | null>(null);
  const markerRefs = useRef(new Map<string, NativeMarker>());
  const statusCallbackRef = useRef(onStatusChange);
  const [status, setStatus] = useState<"loading" | "ready" | "error">("loading");
  statusCallbackRef.current = onStatusChange;
  const markerSignature = JSON.stringify(markers);

  const report = (nextStatus: "loading" | "ready" | "error", message?: string) => {
    setStatus(nextStatus);
    statusCallbackRef.current?.(nextStatus, message);
  };

  useEffect(() => {
    const onAuthFailure = () => report("error", "Google Maps authentication failed.");
    authFailureListeners.add(onAuthFailure);
    return () => { authFailureListeners.delete(onAuthFailure); };
  }, []);

  useEffect(() => {
    if (!apiKey) {
      report("error", "Google Maps is not configured.");
      return;
    }
    if (!hostRef.current) return;
    if (!markers.length && !center) {
      report("error", "Google Maps requires a center when no markers are provided.");
      return;
    }
    if (!markers.length) {
      markerRefs.current.forEach((marker) => { marker.map = null; });
      markerRefs.current.clear();
    }
    let cancelled = false;
    report("loading");
      const first = markers[0];
      const mapCenter = center ?? { latitude: first.latitude, longitude: first.longitude };
    loadGoogleMaps(apiKey).then(async (maps) => {
      const libraries = Promise.all([
        maps.importLibrary("maps") as Promise<MapsLibrary>,
        maps.importLibrary("marker") as Promise<MarkerLibrary>,
      ]);
      const [{ Map }, { AdvancedMarkerElement }] = await Promise.race([
        libraries,
        new Promise<never>((_, reject) => window.setTimeout(() => reject(new Error("Google Maps libraries took too long to load.")), LOAD_TIMEOUT_MS)),
      ]);
      if (googleMapsAuthFailed) throw new Error("Google Maps authentication failed.");
      if (cancelled || !hostRef.current) return;
      if (mapRef.current && mapIdRef.current !== mapId) {
        markerRefs.current.forEach((marker) => { marker.map = null; });
        markerRefs.current.clear();
        mapRef.current = null;
        hostRef.current.replaceChildren();
      }
      if (!mapRef.current) {
        mapRef.current = new Map(hostRef.current, { center: { lat: mapCenter.latitude, lng: mapCenter.longitude }, zoom, mapId, streetViewControl: false, mapTypeControl: false, fullscreenControl: false });
        mapIdRef.current = mapId;
      }
      else { mapRef.current.setCenter({ lat: mapCenter.latitude, lng: mapCenter.longitude }); mapRef.current.setZoom(zoom); }
      const incoming = new Set(markers.map((marker) => marker.id));
      markerRefs.current.forEach((marker, id) => { if (!incoming.has(id)) { marker.map = null; markerRefs.current.delete(id); } });
      markers.forEach((marker) => {
        const position = { lat: marker.latitude, lng: marker.longitude };
        const existing = markerRefs.current.get(marker.id);
        if (existing) { existing.position = position; existing.title = marker.label || undefined; }
        else markerRefs.current.set(marker.id, new AdvancedMarkerElement({ map: mapRef.current as NativeMap, position, title: marker.label || undefined }));
      });
      report("ready");
    }).catch((error: unknown) => {
      if (!cancelled) {
        const message = error instanceof Error ? error.message : "Google Maps could not be loaded.";
        report("error", message);
      }
    });
    return () => { cancelled = true; };
  }, [apiKey, center?.latitude, center?.longitude, mapId, markerSignature, zoom]);

  useEffect(() => () => {
    markerRefs.current.forEach((marker) => { marker.map = null; });
    markerRefs.current.clear();
    mapRef.current = null;
    mapIdRef.current = null;
    hostRef.current?.replaceChildren();
  }, []);

  return <div ref={hostRef} style={{ height, width: "100%" }} data-map-status={status} />;
}
